import { useState, useEffect } from 'react'
import { adminApi } from '../../services/api'
import { useAuth } from '../../hooks/useAuth'
import type { Team, Event, Registration } from '../../types'
import LoadingSpinner from '../../components/common/LoadingSpinner'

export default function AdminTeams() {
  const { getToken } = useAuth()
  const [teams, setTeams] = useState<Team[]>([])
  const [events, setEvents] = useState<Event[]>([])
  const [unassignedRegistrations, setUnassignedRegistrations] = useState<
    Registration[]
  >([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedEventId, setSelectedEventId] = useState<string>('')

  // Create team modal
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newTeamName, setNewTeamName] = useState('')
  const [newTeamNotes, setNewTeamNotes] = useState('')
  const [creatingTeam, setCreatingTeam] = useState(false)

  // Add member modal
  const [showAddMemberModal, setShowAddMemberModal] = useState(false)
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null)
  const [selectedRegistrationIds, setSelectedRegistrationIds] = useState<Set<string>>(new Set())
  const [addingMember, setAddingMember] = useState(false)
  const [memberSearchQuery, setMemberSearchQuery] = useState('')
  const [deletingTeam, setDeletingTeam] = useState(false)

  // Search
  const [searchQuery, setSearchQuery] = useState('')

  // Email modal
  const [showEmailModal, setShowEmailModal] = useState(false)
  const [emailTeamId, setEmailTeamId] = useState<string | null>(null)
  const [emailMessage, setEmailMessage] = useState('')
  const [sendingEmail, setSendingEmail] = useState(false)
  const [exporting, setExporting] = useState(false)

  const fetchData = async () => {
    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')

      const [teamsData, eventsData, regsData] = await Promise.all([
        adminApi.getTeams(token, selectedEventId || undefined),
        adminApi.getEvents(token),
        adminApi.getRegistrations(token, selectedEventId || undefined),
      ])

      setTeams(teamsData.teams)
      setEvents(eventsData.events)

      // Filter to paid registrations with golfers that aren't on a team
      const unassigned = regsData.registrations.filter(
        (r) =>
          r.status === 'paid' && r.golfersCount > 0 && !r.teamId
      )
      setUnassignedRegistrations(unassigned)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [getToken, selectedEventId])

  const handleCreateTeam = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedEventId) {
      alert('Please select an event first')
      return
    }

    setCreatingTeam(true)
    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')

      await adminApi.createTeam(token, selectedEventId, newTeamName, newTeamNotes)
      setShowCreateModal(false)
      setNewTeamName('')
      setNewTeamNotes('')
      fetchData()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create team')
    } finally {
      setCreatingTeam(false)
    }
  }

  const handleAddMembers = async () => {
    if (!selectedTeamId || selectedRegistrationIds.size === 0) return

    setAddingMember(true)
    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')

      // Add members one by one
      for (const registrationId of selectedRegistrationIds) {
        await adminApi.addTeamMember(token, selectedTeamId, registrationId)
      }

      setShowAddMemberModal(false)
      setSelectedTeamId(null)
      setSelectedRegistrationIds(new Set())
      fetchData()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to add member')
    } finally {
      setAddingMember(false)
    }
  }

  // Get the team being edited for the modal
  const getSelectedTeam = () => {
    if (!selectedTeamId) return null
    return teams.find(t => t.teamId === selectedTeamId) || null
  }

  // Calculate remaining spots on a team
  const getRemainingSpots = (team: Team | null) => {
    if (!team) return 4
    return 4 - getTotalGolfers(team)
  }

  // Calculate total golfers in current selection
  const getSelectedGolfersCount = () => {
    return unassignedRegistrations
      .filter(r => selectedRegistrationIds.has(r.registrationId))
      .reduce((sum, r) => sum + r.golfersCount, 0)
  }

  // Check if a registration can be selected (wouldn't exceed remaining spots)
  const canSelectRegistration = (reg: Registration) => {
    if (selectedRegistrationIds.has(reg.registrationId)) return true // Can always deselect
    const selectedTeam = getSelectedTeam()
    const remaining = getRemainingSpots(selectedTeam)
    const currentSelected = getSelectedGolfersCount()
    return currentSelected + reg.golfersCount <= remaining
  }

  // Toggle registration selection
  const toggleRegistrationSelection = (registrationId: string) => {
    setSelectedRegistrationIds(prev => {
      const newSet = new Set(prev)
      if (newSet.has(registrationId)) {
        newSet.delete(registrationId)
      } else {
        newSet.add(registrationId)
      }
      return newSet
    })
  }

  const handleRemoveMember = async (teamId: string, registrationId: string) => {
    if (!confirm('Are you sure you want to remove this member from the team?')) {
      return
    }

    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')

      await adminApi.removeTeamMember(token, teamId, registrationId)
      fetchData()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to remove member')
    }
  }

  const handleSendEmail = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!emailTeamId || !emailMessage.trim()) return

    setSendingEmail(true)
    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')

      const result = await adminApi.emailTeam(token, emailTeamId, emailMessage)
      alert(`Email sent to ${result.recipientCount} recipients`)
      setShowEmailModal(false)
      setEmailTeamId(null)
      setEmailMessage('')
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to send email')
    } finally {
      setSendingEmail(false)
    }
  }

  if (loading) {
    return (
      <div className="py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="card text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <button onClick={fetchData} className="btn btn-primary">
          Try Again
        </button>
      </div>
    )
  }

  const getTotalGolfers = (team: Team) => {
    return (team.members || []).reduce(
      (sum, m) => sum + (m.golfersCount || 0),
      0
    )
  }

  const handleDeleteTeam = async (teamId: string, teamName: string) => {
    if (!confirm(`Delete team "${teamName}"? This cannot be undone.`)) return
    setDeletingTeam(true)
    try {
      const token = await getToken()
      if (!token) return
      await adminApi.deleteTeam(token, teamId)
      fetchData()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete team')
    } finally {
      setDeletingTeam(false)
    }
  }

  const handleMoveMember = async (newTeamId: string, registrationId: string) => {
    try {
      const token = await getToken()
      if (!token) return
      await adminApi.addTeamMember(token, newTeamId, registrationId)
      fetchData()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to move member')
    }
  }

  // Filter teams by search query
  const filteredTeams = teams.filter(team => {
    if (!searchQuery) return true
    const query = searchQuery.toLowerCase()
    if (team.teamName.toLowerCase().includes(query)) return true
    return (team.members || []).some(m =>
      m.primaryContact.name?.toLowerCase().includes(query) ||
      m.primaryContact.email?.toLowerCase().includes(query) ||
      m.primaryContact.phone?.includes(query) ||
      m.golfers.some(g =>
        g.name?.toLowerCase().includes(query) ||
        g.email?.toLowerCase().includes(query) ||
        g.phone?.includes(query)
      )
    )
  })

  const handleExport = async () => {
    setExporting(true)
    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')
      const blob = await adminApi.exportTeams(token, selectedEventId || undefined)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `teams-${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to export')
    } finally {
      setExporting(false)
    }
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Teams</h1>

        <div className="flex gap-3">
          <select
            className="input w-auto"
            value={selectedEventId}
            onChange={(e) => setSelectedEventId(e.target.value)}
          >
            <option value="">All Events</option>
            {events.map((event) => (
              <option key={event.eventId} value={event.eventId}>
                {event.name}
              </option>
            ))}
          </select>

          <button
            onClick={handleExport}
            disabled={exporting}
            className="btn btn-secondary"
          >
            {exporting ? (
              <span className="flex items-center gap-2">
                <LoadingSpinner size="sm" />
                Exporting...
              </span>
            ) : (
              'Export CSV'
            )}
          </button>

          <button
            onClick={() => setShowCreateModal(true)}
            className="btn btn-primary"
            disabled={!selectedEventId}
          >
            Create Team
          </button>
        </div>
      </div>

      {/* Summary */}
      <div className="card mb-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <p className="text-2xl font-bold text-gray-800">{teams.length}</p>
            <p className="text-sm text-gray-500">Teams</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-green-600">
              {teams.reduce((sum, t) => sum + getTotalGolfers(t), 0)}
            </p>
            <p className="text-sm text-gray-500">Assigned Golfers</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-yellow-600">
              {unassignedRegistrations.reduce(
                (sum, r) => sum + r.golfersCount,
                0
              )}
            </p>
            <p className="text-sm text-gray-500">Unassigned Golfers</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-blue-600">
              {unassignedRegistrations.length}
            </p>
            <p className="text-sm text-gray-500">Unassigned Registrations</p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="card mb-6">
        <input
          type="text"
          className="input"
          placeholder="Search by golfer name, email, or phone..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Teams Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {filteredTeams.map((team) => (
          <div key={team.teamId} className="card">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-lg font-bold text-gray-800">
                  {team.teamName}
                </h2>
                <p className="text-sm text-gray-500">
                  {getTotalGolfers(team)}/4 golfers
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setEmailTeamId(team.teamId)
                    setShowEmailModal(true)
                  }}
                  className="text-sla-blue hover:underline text-sm"
                  disabled={(team.members || []).length === 0}
                >
                  Email
                </button>
                {getTotalGolfers(team) < 4 && (
                  <button
                    onClick={() => {
                      setSelectedTeamId(team.teamId)
                      setShowAddMemberModal(true)
                    }}
                    className="text-green-600 hover:underline text-sm"
                  >
                    + Add
                  </button>
                )}
              </div>
            </div>

            {team.notes && (
              <p className="text-sm text-gray-600 mb-4 italic">
                {team.notes}
              </p>
            )}

            {(team.members || []).length === 0 ? (
              <div className="flex items-center justify-between">
                <p className="text-gray-400 text-sm">No members yet</p>
                <button
                  onClick={() => handleDeleteTeam(team.teamId, team.teamName)}
                  disabled={deletingTeam}
                  className="text-red-500 hover:underline text-xs"
                >
                  {deletingTeam ? 'Deleting...' : 'Delete Team'}
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {(team.members || []).map((member) => (
                  <div
                    key={member.registrationId}
                    className="border rounded-lg p-3 bg-gray-50"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">
                          {member.primaryContact.name}
                        </p>
                        <p className="text-sm text-gray-600">
                          {member.golfersCount} golfer(s)
                        </p>
                        {member.golfers.map((g, i) => (
                          <p key={i} className="text-xs text-gray-500">
                            {g.name}
                          </p>
                        ))}
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <button
                          onClick={() =>
                            handleRemoveMember(team.teamId, member.registrationId)
                          }
                          className="text-red-500 hover:underline text-xs"
                        >
                          Remove
                        </button>
                        <select
                          className="text-xs border rounded px-1 py-0.5 text-blue-600"
                          value=""
                          onChange={(e) => {
                            if (e.target.value) handleMoveMember(e.target.value, member.registrationId)
                          }}
                        >
                          <option value="">Move to...</option>
                          {teams
                            .filter(t => t.teamId !== team.teamId && getTotalGolfers(t) + member.golfersCount <= 4)
                            .map(t => (
                              <option key={t.teamId} value={t.teamId}>
                                {t.teamName} ({getTotalGolfers(t)}/4)
                              </option>
                            ))
                          }
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {filteredTeams.length === 0 && (
          <div className="col-span-2 card text-center py-8 text-gray-500">
            No teams found.{' '}
            {selectedEventId
              ? 'Create your first team!'
              : 'Select an event first.'}
          </div>
        )}
      </div>

      {/* Create Team Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              Create New Team
            </h2>

            <form onSubmit={handleCreateTeam} className="space-y-4">
              <div>
                <label className="label">Team Name *</label>
                <input
                  type="text"
                  className="input"
                  value={newTeamName}
                  onChange={(e) => setNewTeamName(e.target.value)}
                  required
                  placeholder="e.g., The Birdies"
                />
              </div>

              <div>
                <label className="label">Notes (optional)</label>
                <textarea
                  className="input"
                  rows={2}
                  value={newTeamNotes}
                  onChange={(e) => setNewTeamNotes(e.target.value)}
                  placeholder="Any notes about this team"
                />
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creatingTeam}
                  className="btn btn-primary"
                >
                  {creatingTeam ? 'Creating...' : 'Create Team'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Member Modal */}
      {showAddMemberModal && (() => {
        const selectedTeam = getSelectedTeam()
        const remainingSpots = getRemainingSpots(selectedTeam)
        const selectedCount = getSelectedGolfersCount()

        // Collect all golfer emails already on this team for duplicate detection
        const teamGolferEmails = new Set<string>()
        for (const member of selectedTeam?.members || []) {
          for (const g of member.golfers || []) {
            if (g.email) teamGolferEmails.add(g.email.toLowerCase().trim())
          }
        }

        // Check if a registration has golfer email overlap with the team
        const getDuplicateGolfers = (reg: Registration): string[] => {
          return reg.golfers
            .filter(g => g.email && teamGolferEmails.has(g.email.toLowerCase().trim()))
            .map(g => g.name || g.email)
        }

        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full p-6 max-h-[80vh] overflow-y-auto">
              <h2 className="text-xl font-bold text-gray-800 mb-2">
                Add Team Members
              </h2>
              {selectedTeam && (
                <p className="text-sm text-gray-600 mb-4">
                  Adding to: <span className="font-medium">{selectedTeam.teamName}</span>
                  <span className="ml-2 text-gray-500">
                    ({remainingSpots} spot{remainingSpots !== 1 ? 's' : ''} remaining)
                  </span>
                </p>
              )}

              {unassignedRegistrations.length === 0 ? (
                <p className="text-gray-500">
                  No unassigned registrations available.
                </p>
              ) : (
                <>
                  <input
                    type="text"
                    className="input mb-3"
                    placeholder="Search by name, email, or phone..."
                    value={memberSearchQuery}
                    onChange={(e) => setMemberSearchQuery(e.target.value)}
                  />
                  <p className="text-sm text-gray-600 mb-3">
                    Select registrations to add to this team:
                  </p>
                  <div className="space-y-2">
                    {unassignedRegistrations.filter((reg) => {
                      if (!memberSearchQuery) return true
                      const q = memberSearchQuery.toLowerCase()
                      return (
                        reg.primaryContact.name?.toLowerCase().includes(q) ||
                        reg.primaryContact.email?.toLowerCase().includes(q) ||
                        reg.primaryContact.phone?.includes(q) ||
                        reg.golfers.some(g =>
                          g.name?.toLowerCase().includes(q) ||
                          g.email?.toLowerCase().includes(q) ||
                          g.phone?.includes(q)
                        ) ||
                        reg.teamName?.toLowerCase().includes(q)
                      )
                    }).map((reg) => {
                      const isSelected = selectedRegistrationIds.has(reg.registrationId)
                      const canSelect = canSelectRegistration(reg)
                      const isDisabled = !canSelect && !isSelected
                      const dupes = getDuplicateGolfers(reg)
                      const hasDuplicates = dupes.length > 0

                      return (
                        <label
                          key={reg.registrationId}
                          className={`border rounded-lg p-3 flex items-start gap-3 transition-colors ${
                            hasDuplicates
                              ? 'border-amber-400 bg-amber-50 cursor-pointer'
                              : isSelected
                                ? 'border-green-500 bg-green-50 cursor-pointer'
                                : isDisabled
                                  ? 'border-gray-200 bg-gray-100 cursor-not-allowed opacity-60'
                                  : 'hover:bg-gray-50 cursor-pointer'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={isDisabled}
                            onChange={() => !isDisabled && toggleRegistrationSelection(reg.registrationId)}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <p className="font-medium">{reg.primaryContact.name}</p>
                            <p className="text-sm text-gray-600">
                              {reg.golfersCount} golfer{reg.golfersCount !== 1 ? 's' : ''}
                            </p>
                            {reg.golfers.map((g, i) => (
                              <p key={i} className="text-xs text-gray-500">
                                {g.name}
                              </p>
                            ))}
                            {reg.teamName && (
                              <p className="text-xs text-blue-600 mt-1">
                                Requested: {reg.teamName}
                              </p>
                            )}
                            {hasDuplicates && (
                              <p className="text-xs text-amber-700 mt-1 font-medium">
                                Duplicate: {dupes.join(', ')} already on this team
                              </p>
                            )}
                            {isDisabled && !hasDuplicates && (
                              <p className="text-xs text-red-500 mt-1">
                                Would exceed remaining spots
                              </p>
                            )}
                          </div>
                        </label>
                      )
                    })}
                  </div>

                  {selectedCount > 0 && (
                    <p className="mt-3 text-sm text-green-700 font-medium">
                      Selected: {selectedCount} golfer{selectedCount !== 1 ? 's' : ''} from {selectedRegistrationIds.size} registration{selectedRegistrationIds.size !== 1 ? 's' : ''}
                    </p>
                  )}
                </>
              )}

              <div className="mt-4 flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowAddMemberModal(false)
                    setSelectedTeamId(null)
                    setSelectedRegistrationIds(new Set())
                    setMemberSearchQuery('')
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                {unassignedRegistrations.length > 0 && (
                  <button
                    onClick={handleAddMembers}
                    disabled={selectedRegistrationIds.size === 0 || addingMember}
                    className="btn btn-primary"
                  >
                    {addingMember ? 'Adding...' : `Add Selected (${selectedCount})`}
                  </button>
                )}
              </div>
            </div>
          </div>
        )
      })()}

      {/* Email Team Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-lg w-full p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              Email Team Members
            </h2>

            <form onSubmit={handleSendEmail} className="space-y-4">
              <div>
                <label className="label">Message *</label>
                <textarea
                  className="input"
                  rows={6}
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  required
                  placeholder="Enter your message to the team..."
                />
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowEmailModal(false)
                    setEmailTeamId(null)
                    setEmailMessage('')
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={sendingEmail}
                  className="btn btn-primary"
                >
                  {sendingEmail ? 'Sending...' : 'Send Email'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
